import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  X, ChevronRight, ChevronLeft, Search, Calendar, 
  CreditCard, Star, User, FileText, Euro, CheckCircle, Sparkles, ArrowRight
} from 'lucide-react';

// Translations for onboarding
const ONBOARDING_TRANSLATIONS = {
  fr: {
    client: [
      {
        id: 'welcome',
        title: 'Bienvenue sur AFY !',
        description: 'Découvrez comment réserver des services personnalisés en quelques clics.',
        tips: [
          'Trouvez des prestataires qualifiés près de chez vous',
          'Réservez en toute sécurité avec paiement protégé',
          'Laissez des avis pour aider la communauté'
        ]
      },
      {
        id: 'search',
        title: 'Rechercher un prestataire',
        description: 'Explorez les catégories : Chef à domicile, Coach sportif, Coiffeur, Nounou...',
        tips: [
          'Filtrez par catégorie, ville ou disponibilité',
          'Consultez les profils et portfolios',
          'Vérifiez les avis et notes des autres clients'
        ],
        actionLabel: 'Voir les prestataires',
        actionPath: '/providers'
      },
      {
        id: 'booking',
        title: 'Réserver un service',
        description: 'Sélectionnez une date et un créneau horaire qui vous convient.',
        tips: [
          'Choisissez la date et l\'heure',
          'Ajoutez des notes pour le prestataire',
          'Appliquez un code promo si vous en avez un'
        ]
      },
      {
        id: 'payment',
        title: 'Paiement sécurisé',
        description: 'Payez en toute sécurité via Stripe. Votre paiement est protégé.',
        tips: [
          'Paiement par carte bancaire sécurisé',
          'Facture détaillée disponible',
          'Annulation possible selon conditions'
        ]
      },
      {
        id: 'review',
        title: 'Évaluez le service',
        description: 'Laissez un avis pour aider les autres utilisateurs.',
        tips: [
          'Notez de 1 à 5 étoiles',
          'Partagez votre expérience',
          'Aidez la communauté AFY'
        ],
        actionLabel: 'Mes réservations',
        actionPath: '/bookings'
      }
    ],
    provider: [
      {
        id: 'welcome',
        title: 'Bienvenue, nouveau prestataire !',
        description: 'Ce tutoriel vous guidera pour configurer votre profil et recevoir des réservations.',
        tips: [
          'Complétez votre profil pour attirer des clients',
          'Configurez Stripe pour recevoir vos paiements',
          'Attendez la validation admin pour être visible'
        ]
      },
      {
        id: 'profile',
        title: 'Créez votre profil',
        description: 'Un profil complet augmente vos chances d\'être choisi.',
        tips: [
          'Photo de profil professionnelle',
          'Bio détaillée et engageante',
          'Listez vos spécialités et langues'
        ],
        actionLabel: 'Mon profil',
        actionPath: '/provider/profile'
      },
      {
        id: 'pricing',
        title: 'Définissez vos tarifs',
        description: 'Tarification à l\'heure ou par personne selon votre service.',
        tips: [
          'Tarif horaire OU par personne',
          'Heures minimum par réservation',
          'Capacité maximale de clients'
        ],
        actionLabel: 'Configurer tarifs',
        actionPath: '/provider/profile'
      },
      {
        id: 'stripe',
        title: 'Configurez Stripe',
        description: 'Connectez votre compte Stripe pour recevoir vos paiements.',
        tips: [
          'Créez votre compte Stripe Express',
          'Complétez la vérification d\'identité',
          'Ajoutez vos coordonnées bancaires'
        ],
        actionLabel: 'Configurer Stripe',
        actionPath: '/provider/stripe-setup',
        important: true
      },
      {
        id: 'portfolio',
        title: 'Partagez votre travail',
        description: 'Montrez vos réalisations pour attirer les clients.',
        tips: [
          'Publiez des photos de qualité',
          'Racontez vos expériences',
          'Interagissez avec les clients'
        ],
        actionLabel: 'Mon portfolio',
        actionPath: '/provider/blog'
      },
      {
        id: 'verification',
        title: 'Validation finale',
        description: 'Un administrateur validera votre profil sous 24-48h.',
        tips: [
          'Vérification sous 24-48h',
          'Notification par email',
          'Commencez à recevoir des réservations !'
        ]
      }
    ],
    buttons: {
      skip: 'Passer le tutoriel',
      next: 'Suivant',
      previous: 'Précédent',
      finish: 'Terminer'
    }
  },
  en: {
    client: [
      {
        id: 'welcome',
        title: 'Welcome to AFY!',
        description: 'Discover how to book personalized services in just a few clicks.',
        tips: [
          'Find qualified providers near you',
          'Book safely with protected payment',
          'Leave reviews to help the community'
        ]
      },
      {
        id: 'search',
        title: 'Search for a provider',
        description: 'Explore categories: Private Chef, Sports Coach, Hairdresser, Nanny...',
        tips: [
          'Filter by category, city or availability',
          'View profiles and portfolios',
          'Check reviews and ratings'
        ],
        actionLabel: 'View providers',
        actionPath: '/providers'
      },
      {
        id: 'booking',
        title: 'Book a service',
        description: 'Select a date and time slot that suits you.',
        tips: [
          'Choose date and time',
          'Add notes for the provider',
          'Apply a promo code if you have one'
        ]
      },
      {
        id: 'payment',
        title: 'Secure payment',
        description: 'Pay securely via Stripe. Your payment is protected.',
        tips: [
          'Secure credit card payment',
          'Detailed invoice available',
          'Cancellation possible under conditions'
        ]
      },
      {
        id: 'review',
        title: 'Rate the service',
        description: 'Leave a review to help other users.',
        tips: [
          'Rate from 1 to 5 stars',
          'Share your experience',
          'Help the AFY community'
        ],
        actionLabel: 'My bookings',
        actionPath: '/bookings'
      }
    ],
    provider: [
      {
        id: 'welcome',
        title: 'Welcome, new provider!',
        description: 'This tutorial will guide you to set up your profile and receive bookings.',
        tips: [
          'Complete your profile to attract clients',
          'Set up Stripe to receive payments',
          'Wait for admin validation to be visible'
        ]
      },
      {
        id: 'profile',
        title: 'Create your profile',
        description: 'A complete profile increases your chances of being chosen.',
        tips: [
          'Professional profile photo',
          'Detailed and engaging bio',
          'List your specialties and languages'
        ],
        actionLabel: 'My profile',
        actionPath: '/provider/profile'
      },
      {
        id: 'pricing',
        title: 'Set your rates',
        description: 'Hourly or per-person pricing depending on your service.',
        tips: [
          'Hourly OR per-person rate',
          'Minimum hours per booking',
          'Maximum client capacity'
        ],
        actionLabel: 'Configure rates',
        actionPath: '/provider/profile'
      },
      {
        id: 'stripe',
        title: 'Configure Stripe',
        description: 'Connect your Stripe account to receive payments.',
        tips: [
          'Create your Stripe Express account',
          'Complete identity verification',
          'Add your bank details'
        ],
        actionLabel: 'Configure Stripe',
        actionPath: '/provider/stripe-setup',
        important: true
      },
      {
        id: 'portfolio',
        title: 'Share your work',
        description: 'Show your achievements to attract clients.',
        tips: [
          'Post quality photos',
          'Share your experiences',
          'Interact with clients'
        ],
        actionLabel: 'My portfolio',
        actionPath: '/provider/blog'
      },
      {
        id: 'verification',
        title: 'Final validation',
        description: 'An admin will validate your profile within 24-48h.',
        tips: [
          'Verification within 24-48h',
          'Email notification',
          'Start receiving bookings!'
        ]
      }
    ],
    buttons: {
      skip: 'Skip tutorial',
      next: 'Next',
      previous: 'Previous',
      finish: 'Finish'
    }
  },
  de: {
    client: [
      {
        id: 'welcome',
        title: 'Willkommen bei AFY!',
        description: 'Entdecken Sie, wie Sie personalisierte Dienste mit wenigen Klicks buchen.',
        tips: [
          'Finden Sie qualifizierte Anbieter in Ihrer Nähe',
          'Buchen Sie sicher mit geschützter Zahlung',
          'Hinterlassen Sie Bewertungen für die Community'
        ]
      },
      {
        id: 'search',
        title: 'Anbieter suchen',
        description: 'Erkunden Sie Kategorien: Privatkoch, Sportcoach, Friseur, Kindermädchen...',
        tips: [
          'Filtern nach Kategorie, Stadt oder Verfügbarkeit',
          'Profile und Portfolios ansehen',
          'Bewertungen und Ratings prüfen'
        ],
        actionLabel: 'Anbieter ansehen',
        actionPath: '/providers'
      },
      {
        id: 'booking',
        title: 'Service buchen',
        description: 'Wählen Sie ein Datum und eine Uhrzeit, die Ihnen passt.',
        tips: [
          'Datum und Uhrzeit wählen',
          'Notizen für den Anbieter hinzufügen',
          'Promo-Code anwenden falls vorhanden'
        ]
      },
      {
        id: 'payment',
        title: 'Sichere Zahlung',
        description: 'Zahlen Sie sicher über Stripe. Ihre Zahlung ist geschützt.',
        tips: [
          'Sichere Kreditkartenzahlung',
          'Detaillierte Rechnung verfügbar',
          'Stornierung unter Bedingungen möglich'
        ]
      },
      {
        id: 'review',
        title: 'Service bewerten',
        description: 'Hinterlassen Sie eine Bewertung, um anderen Nutzern zu helfen.',
        tips: [
          'Bewerten Sie von 1 bis 5 Sternen',
          'Teilen Sie Ihre Erfahrung',
          'Helfen Sie der AFY-Community'
        ],
        actionLabel: 'Meine Buchungen',
        actionPath: '/bookings'
      }
    ],
    provider: [
      {
        id: 'welcome',
        title: 'Willkommen, neuer Anbieter!',
        description: 'Dieses Tutorial hilft Ihnen, Ihr Profil einzurichten und Buchungen zu erhalten.',
        tips: [
          'Vervollständigen Sie Ihr Profil für Kunden',
          'Richten Sie Stripe für Zahlungen ein',
          'Warten Sie auf Admin-Validierung'
        ]
      },
      {
        id: 'profile',
        title: 'Profil erstellen',
        description: 'Ein vollständiges Profil erhöht Ihre Chancen.',
        tips: [
          'Professionelles Profilfoto',
          'Detaillierte und ansprechende Bio',
          'Spezialitäten und Sprachen auflisten'
        ],
        actionLabel: 'Mein Profil',
        actionPath: '/provider/profile'
      },
      {
        id: 'pricing',
        title: 'Preise festlegen',
        description: 'Stunden- oder Pro-Person-Preise je nach Service.',
        tips: [
          'Stundensatz ODER Pro-Person-Preis',
          'Mindeststunden pro Buchung',
          'Maximale Kundenkapazität'
        ],
        actionLabel: 'Preise konfigurieren',
        actionPath: '/provider/profile'
      },
      {
        id: 'stripe',
        title: 'Stripe konfigurieren',
        description: 'Verbinden Sie Ihr Stripe-Konto für Zahlungen.',
        tips: [
          'Stripe Express-Konto erstellen',
          'Identitätsprüfung abschließen',
          'Bankdaten hinzufügen'
        ],
        actionLabel: 'Stripe konfigurieren',
        actionPath: '/provider/stripe-setup',
        important: true
      },
      {
        id: 'portfolio',
        title: 'Arbeit zeigen',
        description: 'Zeigen Sie Ihre Leistungen, um Kunden anzuziehen.',
        tips: [
          'Qualitätsfotos posten',
          'Erfahrungen teilen',
          'Mit Kunden interagieren'
        ],
        actionLabel: 'Mein Portfolio',
        actionPath: '/provider/blog'
      },
      {
        id: 'verification',
        title: 'Finale Validierung',
        description: 'Ein Admin wird Ihr Profil innerhalb von 24-48h validieren.',
        tips: [
          'Verifizierung innerhalb 24-48h',
          'E-Mail-Benachrichtigung',
          'Buchungen erhalten!'
        ]
      }
    ],
    buttons: {
      skip: 'Tutorial überspringen',
      next: 'Weiter',
      previous: 'Zurück',
      finish: 'Fertig'
    }
  }
};

// Step icons mapping
const STEP_ICONS = {
  welcome: Sparkles,
  search: Search,
  booking: Calendar,
  payment: CreditCard,
  review: Star,
  profile: User,
  pricing: Euro,
  stripe: CreditCard,
  portfolio: FileText,
  verification: CheckCircle
};

// Step colors mapping
const STEP_COLORS = {
  welcome: 'from-violet-500 to-purple-600',
  search: 'from-blue-500 to-cyan-500',
  booking: 'from-green-500 to-emerald-500',
  payment: 'from-amber-500 to-orange-500',
  review: 'from-pink-500 to-rose-500',
  profile: 'from-blue-500 to-cyan-500',
  pricing: 'from-green-500 to-emerald-500',
  stripe: 'from-amber-500 to-orange-500',
  portfolio: 'from-pink-500 to-rose-500',
  verification: 'from-emerald-500 to-teal-500'
};

export default function OnboardingTutorial({ userRole = 'client', onComplete, isOpen, onClose }) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(isOpen);
  
  // Get translations based on current language (fallback to French)
  const translations = ONBOARDING_TRANSLATIONS[language] || ONBOARDING_TRANSLATIONS['fr'];
  const steps = userRole === 'provider' ? translations.provider : translations.client;
  const buttons = translations.buttons;
  
  const step = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;
  const IconComponent = STEP_ICONS[step.id] || Sparkles;
  const stepColor = STEP_COLORS[step.id] || 'from-violet-500 to-purple-600';
  
  useEffect(() => {
    setIsVisible(isOpen);
  }, [isOpen]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem(`onboarding_${userRole}_completed`, 'true');
    setIsVisible(false);
    onComplete?.();
    onClose?.();
  };

  const handleSkip = () => {
    localStorage.setItem(`onboarding_${userRole}_skipped`, 'true');
    setIsVisible(false);
    onClose?.();
  };

  const handleAction = () => {
    if (step.actionPath) {
      navigate(step.actionPath);
      setIsVisible(false);
      onClose?.();
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in" data-testid="onboarding-tutorial">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
      </div>

      <Card className="relative w-full max-w-lg bg-background/95 backdrop-blur border-2 shadow-2xl animate-scale-in">
        {/* Close button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-muted-foreground" />
        </button>

        {/* Progress bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-slate-200 dark:bg-slate-700 rounded-t-lg overflow-hidden">
          <div 
            className={`h-full bg-gradient-to-r ${stepColor} transition-all duration-500`}
            style={{ width: `${progress}%` }}
          />
        </div>

        <CardContent className="pt-10 pb-6">
          {/* Step indicator */}
          <div className="flex justify-center gap-2 mb-6">
            {steps.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setCurrentStep(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === currentStep 
                    ? 'w-8 bg-gradient-to-r ' + stepColor
                    : i < currentStep 
                      ? 'bg-accent' 
                      : 'bg-slate-300 dark:bg-slate-600'
                }`}
              />
            ))}
          </div>

          {/* Icon */}
          <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br ${stepColor} flex items-center justify-center shadow-lg`}>
            <IconComponent className="w-10 h-10 text-white" />
          </div>

          {/* Content */}
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <h2 className="font-heading text-2xl font-bold">{step.title}</h2>
              {step.important && (
                <Badge variant="destructive" className="text-xs">Important</Badge>
              )}
            </div>
            <p className="text-muted-foreground leading-relaxed">
              {step.description}
            </p>
          </div>

          {/* Tips */}
          <div className="space-y-2 mb-6">
            {step.tips.map((tip, i) => (
              <div 
                key={i}
                className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg"
              >
                <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${stepColor} flex items-center justify-center shrink-0`}>
                  <span className="text-xs font-bold text-white">{i + 1}</span>
                </div>
                <span className="text-sm">{tip}</span>
              </div>
            ))}
          </div>

          {/* Action button */}
          {step.actionLabel && step.actionPath && (
            <Button 
              onClick={handleAction}
              variant="outline"
              className="w-full mb-4"
            >
              {step.actionLabel}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={handlePrev}
              disabled={currentStep === 0}
              className="gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              {buttons.previous}
            </Button>

            <span className="text-sm text-muted-foreground">
              {currentStep + 1} / {steps.length}
            </span>

            {currentStep < steps.length - 1 ? (
              <Button
                onClick={handleNext}
                className={`gap-2 bg-gradient-to-r ${stepColor} hover:opacity-90`}
              >
                {buttons.next}
                <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={handleComplete}
                className="gap-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:opacity-90"
              >
                {buttons.finish}
                <CheckCircle className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Skip link */}
          <p className="text-center mt-4">
            <button 
              onClick={handleSkip}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {buttons.skip}
            </button>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// Hook to manage onboarding state
export function useOnboarding(userRole) {
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  useEffect(() => {
    const completed = localStorage.getItem(`onboarding_${userRole}_completed`);
    const skipped = localStorage.getItem(`onboarding_${userRole}_skipped`);
    
    // Show onboarding if not completed or skipped
    if (!completed && !skipped) {
      // Small delay to let the page render first
      const timer = setTimeout(() => setShowOnboarding(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [userRole]);

  const resetOnboarding = () => {
    localStorage.removeItem(`onboarding_${userRole}_completed`);
    localStorage.removeItem(`onboarding_${userRole}_skipped`);
    setShowOnboarding(true);
  };

  return {
    showOnboarding,
    setShowOnboarding,
    resetOnboarding
  };
}
